import React, { FunctionComponent } from 'react';
import { Route, Redirect, RouteProps } from 'react-router';
import { useStore } from 'effector-react';
import authStore from '@stores/auth';
import routes from '@config/routes';
import useGuards from '@hooks/useGuards';

type AuthRouteProps = RouteProps;

const moderatorAvailablePaths = [
  routes.postList.path,
  routes.postDetail.path,
  routes.spamCommon.path,
  routes.spam.path,
  routes.spamComments.path,
  routes.spamUsers.path,
  routes.spamStatuses.path,
  routes.games.path,
  routes.userDetail.path,

  routes.tribes.path,
  routes.tribesMembers.path,
  routes.chatDetail.path,
];

const AuthRoute: FunctionComponent<AuthRouteProps> = ({ children, render, ...rest }) => {
  const auth = useStore(authStore);
  const isAuth = !!auth.user;
  const { path } = { ...rest };
  const { isCommunityManager, isModerator } = useGuards();

  const currentPath = path as string;

  const isModeratorAvailablePath = moderatorAvailablePaths.includes(currentPath);

  const hasPermission =
    !(isCommunityManager && currentPath === routes.featuring.path) ||
    (isModerator && isModeratorAvailablePath);

  const toRootRoute = (
    <Route
      {...rest}
      render={({ location }) => (
        <Redirect
          to={{
            pathname: routes.root.path,
            state: { from: location },
          }}
        />
      )}
    />
  );

  const toPostRoute = (
    <Route
      {...rest}
      render={({ location }) => (
        <Redirect
          to={{
            pathname: routes.postList.path,
            state: { from: location },
          }}
        />
      )}
    />
  );

  const toLoginRoute = (
    <Route
      {...rest}
      render={({ location }) => (
        <Redirect
          to={{
            pathname: routes.login.path,
            state: { from: location },
          }}
        />
      )}
    />
  );

  const toOriginalRoute = <Route {...rest} render={render} />;

  if (isAuth) {
    if (hasPermission) {
      if (isModerator && !isModeratorAvailablePath) {
        return toPostRoute;
      }

      return toOriginalRoute;
    }

    return toRootRoute;
  }

  return toLoginRoute;
};

export default AuthRoute;